import { REAL_SEQUENCES_ALGOLIA_FILTER } from '@/lib/sequences/listingKind';
import { searchClient } from '@/lib/search/instantsearch/algoliaAdapter';
import type { LegacySearchRequests, LegacySearchResponses } from '@/lib/search/algolia/client';

/**
 * Multi-index search service for querying both sequences and displays simultaneously
 * Used by the header search modal for unified search experience
 */

export interface MultiIndexSearchHit {
  objectID?: string | number;
  title?: string;
  name?: string;
  artist_name?: string;
  image_url?: string;
  thumbnail_url?: string;
  display_name?: string;
  city?: string;
  state?: string;
  [key: string]: unknown;
}

export interface MultiIndexSearchResult {
  sequences: MultiIndexSearchHit[];
  displays: MultiIndexSearchHit[];
  sequenceCount: number;
  displayCount: number;
}

/**
 * Query both sequences and displays indexes in a single Algolia API call
 * @param query - Search query string
 * @returns Object containing hits and counts for both indexes
 */
export async function getMultiIndexSuggestions(query: string, page = 0): Promise<MultiIndexSearchResult> {
  // Return empty results for short queries
  if (!searchClient || !query.trim() || query.length < 2) {
    return {
      sequences: [],
      displays: [],
      sequenceCount: 0,
      displayCount: 0,
    };
  }

  try {
    // Perform multi-index search with single API call
    const { results } = (await searchClient.search([
      {
        indexName: 'sequences',
        query: query.trim(),
        params: {
          page,
          hitsPerPage: 5,
          // Sequence suggestions are real sequences, like /sequences/search.
          filters: REAL_SEQUENCES_ALGOLIA_FILTER,
          attributesToRetrieve: [
            'objectID',
            'title',
            'artist_name',
            'image_url',
            'thumbnail_url',
          ],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
          attributesToHighlight: ['title', 'artist_name'],
          analytics: false,
        },
      },
      {
        indexName: 'displays',
        query: query.trim(),
        params: {
          page,
          hitsPerPage: 5,
          attributesToRetrieve: [
            'objectID',
            'display_name',
            'name',
            'city',
            'state',
            'image_url',
          ],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
          attributesToHighlight: ['display_name', 'name', 'city'],
          analytics: false,
        },
      },
    ] as LegacySearchRequests)) as LegacySearchResponses;

    return {
      sequences: results[0]?.hits || [],
      displays: results[1]?.hits || [],
      sequenceCount: results[0]?.nbHits || 0,
      displayCount: results[1]?.nbHits || 0,
    };
  } catch (error) {
    console.error('Algolia multi-index search error:', {
      error,
      query: query.trim(),
      timestamp: new Date().toISOString(),
    });
    return {
      sequences: [],
      displays: [],
      sequenceCount: 0,
      displayCount: 0,
    };
  }
}
